import Vue from 'vue';
import { message } from 'ant-design-vue';
import { loadCmhubResources } from '@/features/cmhub/resources';
import { SIDE_QUERY, resolveRouteSide, withSide } from '@/features/cmhub/navigation';
import VueRouter from 'vue-router';

import { start } from 'qiankun';
import Home from '../views/Home.vue';
import Recommend from '../views/Recommend.vue';
import Base from '../views/Base.vue';
import MicroBase from '../views/MicroBase.vue';
import IndividualCenter from '../views/IndividualCenter.vue';
import { getMicroAppList } from '../micro/index';
import api from '../api';
import { post } from '../utils/http';
import { setAccount } from '../views/login/login-handler';
import { getUiFitment } from '../utils/accessResources';
import { getLogo } from '../utils/intConfiguration';
import { isCmhub } from '@/features/cmhub/config';
import { getUnifiedAuth } from '@/features/cmhub/session';

const originalPush = VueRouter.prototype.push;

VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

Vue.use(VueRouter);

const microRoutes = getMicroAppList().map((app) => (
  {
    path: `${app.activeRule}*`,
    name: `MicroBase_${app.name}`,
    component: MicroBase,
  }
));

const SRM_SUPPLIER_APP_ID = '20030';
const isSrmSupplierPortal = String(getLogo()?.appId || '') === SRM_SUPPLIER_APP_ID;
const publicLandingPath = isSrmSupplierPortal ? '/scmInvestor/tender/hall' : '';

// 仅在 SRM 供应商门户站点将 scmInvestor 的所有页面放进同一个顶层 MicroBase。
// 大厅、详情、我的投标和订单协同共用同一路由记录，页面切换时不会销毁并重挂微应用。
// 其他域名仍由 Base 下的 microRoutes 承接，保持原有布局和登录流程不变。
const publicTenderRoutes = publicLandingPath ? [
  {
    path: '/scmInvestor/*',
    name: 'SrmSupplierPortal',
    component: MicroBase,
  },
] : [];

const routes = [
  ...publicTenderRoutes,
  {
    path: '/',
    component: Base,
    children: [
      {
        path: '/',
        name: 'home',
        meta: {
          title: '首页',
        },
        component: Home,
      },
      {
        path: '/recommend',
        name: 'Recommend',
        meta: {
          title: '关于我们',
        },
        component: Recommend,
      },
      {
        path: '/IndividualCenter',
        name: 'IndividualCenter',
        meta: {
          title: '个人中心',
        },
        component: IndividualCenter,
      },
      ...microRoutes,
    ],
  },
  {
    path: '/login',
    name: 'OmsLogin',
    component: () => import(/* webpackChunkName: "OmsLogin" */ '../views/login/Login.vue'),
  },
  {
    path: '*',
    name: 'Page404',
    component: () => import(/* webpackChunkName: "Page404" */ '../views/404.vue'),
  },
  // 1版本=飞书审批
  {
    path: '/flyingBook',
    name: 'flyingBook',
    component: () => import(/* webpackChunkName: "flyingBook" */ '../views/flyingBook/index.vue'),
  },
  // 1版本=飞书审批
  {
    path: '/openAgain',
    name: 'openAgain',
    component: () => import(/* webpackChunkName: "openAgain" */ '../views/flyingBook/openAgain.vue'),
  },
  // 1版本=飞书审批
  {
    path: '/middle',
    name: 'middle',
    component: () => import(/* webpackChunkName: "middle" */ '../views/flyingBook/middle.vue'),
  },
  // 客端saas 跳转登录页
  {
    path: '/saasLogin',
    name: 'saasLogin',
    component: () => import(/* webpackChunkName: "middle" */ '../views/saas/login.vue'),
  },
  // 2版本 飞书审批
  {
    path: '/optimizeIndex',
    name: 'optimizeIndex',
    component: () => import(/* webpackChunkName: "middle" */ '../views/optimizeIndex/index.vue'),
  },
  // 2版本 飞书审批
  {
    path: '/push',
    name: 'push',
    component: () => import(/* webpackChunkName: "middle" */ '../views/optimizeIndex/push.vue'),
  },
  // 2版本 飞书审批
  {
    path: '/singleSignOn',
    name: 'singleSignOn',
    component: () => import(/* webpackChunkName: "middle" */ '../views/singleSignOn/index.vue'),
  },
  // 三方系统中转
  {
    path: '/tps',
    name: 'tps',
    component: () => import(/* webpackChunkName: "middle" */ '../views/tps/index.vue'),
  },
  // 钉钉免登录
  {
    path: '/dingtalkAuth',
    name: 'dingtalkAuth',
    component: () => import(/* webpackChunkName: "middle" */ '../views/dingtalkauth/index.vue'),
  },
  {
    path: '/downloadApp',
    name: 'downloadApp',
    component: () => import(/* webpackChunkName: "middle" */ '../views/downloadApp/index.vue'),
  },
];
const router = new VueRouter({
  mode: 'history',
  routes,
});

// 需要排除的路由列表（这些路由不应该触发自动跳转）
const excludedPaths = [
  '/login',
  '/saasLogin',
  '/flyingBook',
  '/openAgain',
  '/middle',
  '/optimizeIndex',
  '/push',
  '/singleSignOn',
  '/tps',
  '/dingtalkAuth',
  '/downloadApp',
];

const CUSTOMER_SAAS_ROUTE_PREFIX = '/customerSaas/';
const CUSTOMER_SAAS_APP_ID = '10016';

// 外部系统以 token 直达客端 SaaS 时不会经过登录页，也没有 ticket 换 token 的流程。
// 在基座组件创建前补拉一次当前租户的装修配置，确保左侧菜单读取到正确的 logo/title。
const initCustomerSaasUiFitment = async (to) => {
  if (!to.path.startsWith(CUSTOMER_SAAS_ROUTE_PREFIX) || !localStorage.getItem('token')) return;

  // customerSaas 对应的应用固定为 10016；纯 token 跳转可能未写入 appId。
  localStorage.setItem('appId', CUSTOMER_SAAS_APP_ID);
  await getUiFitment();
};

router.beforeEach(async (to, from, next) => {
  if (isCmhub() && !excludedPaths.includes(to.path) && !getUnifiedAuth()) {
    next('/login');
    return;
  }
  if (isCmhub() && !excludedPaths.includes(to.path)) {
    try {
      const { menus } = await loadCmhubResources();
      const side = resolveRouteSide(to, from, menus, getMicroAppList(), router);
      if (side && to.query[SIDE_QUERY] !== side) {
        next({ ...withSide(router, to.fullPath, side), replace: true });
        return;
      }
    } catch (error) {
      message.error(error.message); next(false); return;
    }
  }
  console.log('main app router ===>', to);

  if (to.path === '/' && publicLandingPath) {
    next({
      path: publicLandingPath,
      replace: true,
    });
    return;
  }

  // SSO ticket 换取 token：url 上带 ticket 时，用 ticket 换 token 并去掉 ticket 参数后重定向
  if (to.query.ticket) {
    try {
      const res = await post(api.ssoExchange, { ssoTicket: to.query.ticket });
      if (res && res.token) {
        localStorage.setItem('token', res.token);
        setAccount(res, {});
        await getUiFitment();
      }
    } catch (e) {
      // ticket 换取 token 失败，继续走后续逻辑
    }
    const query = { ...to.query };
    delete query.ticket;
    next({ path: to.path, query, replace: true });
    return;
  }

  // 标准登录页不支持在已有登录态时静默切换账号。
  // token 按浏览器 origin 隔离，不同子域名的应用会分别跳回各自首页。
  if (to.path === '/login' && localStorage.getItem('token')) {
    next({
      path: '/',
      replace: true,
    });
    return;
  }

  // 判断是否是页面刷新（而不是正常的路由导航）
  // 关键判断：from.matched.length === 0 表示是刷新或首次加载（路由未匹配）
  // 正常的路由跳转时，from.matched.length > 0（因为 from 路由已经匹配过）
  const isPageRefreshOrInitialLoad = from.matched.length === 0;
  if (isPageRefreshOrInitialLoad && !isCmhub()) {
    await initCustomerSaasUiFitment(to);
  }
  // 只在页面刷新或首次加载时检查 hostname 条件并跳转
  // 排除登录页面、404页面和其他特殊页面，以及目标路由本身
  if (isPageRefreshOrInitialLoad &&
    !excludedPaths.includes(to.path) &&
    to.path !== '/delivery/merchantSaasHome' &&
    to.name !== 'Page404' && // 排除404页面
    !to.path.startsWith('/login')) { // 额外检查，确保所有登录相关页面都被排除
    const hostname = window.location.hostname.toString();
    const shouldRedirectToBigDataScreen =
      hostname.includes('sh-dev') ||
      hostname.includes('sh-sit') ||
      hostname.includes('sh.kaigongla');

    // if (shouldRedirectToBigDataScreen) {
    //   // 只在刷新/首次加载时，如果当前路由不是目标路由且不在排除列表中，则跳转
    //   next({
    //     path: '/delivery/merchantSaasHome',
    //     replace: true, // 使用 replace 避免在历史记录中留下当前路由
    //   });
    //   return;
    // }
  }

  start({
    prefetch: true, // prefetch: boolean | 'all' | string[] | function
  });
  next();
});

export default router;
